import RefreshIcon from '@mui/icons-material/Refresh'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { useCallback, useEffect, useRef, useState } from 'react'

import { REFRESH_BUTTON } from '../labels'
import { api } from '../lib/api'
import { relativeDays } from '../lib/format'
import type { PollJob } from '../lib/types'
import { ACCENT, FONT_MONO, INK_SOFT } from '../theme'

interface Props {
  lastCheckedAt: string | null
  today: string
  onDone: () => void
}

/** Cada cuánto se pregunta por el avance. */
const POLL_INTERVAL_MS = 2000

/**
 * Dispara la consulta a SharePoint y sigue su avance.
 *
 * El backend devuelve 202 de inmediato y el trabajo sigue en segundo plano:
 * una corrida completa tarda ~90 s y App Runner corta las peticiones a los
 * 120 s, así que esperar la respuesta dejaría al usuario viendo un error
 * mientras el poll, en realidad, termina bien.
 *
 * Las credenciales de Graph nunca salen del backend; el navegador sólo pide
 * que empiece y pregunta cómo va.
 */
export function RefreshButton({ lastCheckedAt, today, onDone }: Props) {
  const [job, setJob] = useState<PollJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const timer = useRef<number | null>(null)

  const stop = useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current)
      timer.current = null
    }
  }, [])

  const check = useCallback(async () => {
    try {
      const status = await api.pollStatus()
      setJob(status)
      if (status.status === 'running') return

      stop()
      if (status.status === 'error') {
        setError(status.error ?? REFRESH_BUTTON.failed)
      } else if (status.report) {
        const r = status.report
        setResult(
          `${REFRESH_BUTTON.done}: ${r.checked} ${REFRESH_BUTTON.checked}, ` +
            `${r.found} ${REFRESH_BUTTON.found}, ${r.missing} ${REFRESH_BUTTON.missing}` +
            (r.errors ? `, ${r.errors} ${REFRESH_BUTTON.errors}` : ''),
        )
        onDone()
      }
    } catch (e) {
      stop()
      setError(e instanceof Error ? e.message : REFRESH_BUTTON.failed)
    }
  }, [onDone, stop])

  // Al montar: si el cron (o otra pestaña) dejó un poll corriendo, se engancha
  // en vez de mostrar el botón como si no pasara nada.
  useEffect(() => {
    void (async () => {
      try {
        const status = await api.pollStatus()
        if (status.status === 'running') {
          setJob(status)
          timer.current = window.setInterval(() => void check(), POLL_INTERVAL_MS)
        }
      } catch {
        /* sin estado previo: no es un error que valga la pena mostrar */
      }
    })()
    return stop
  }, [check, stop])

  async function start() {
    setError(null)
    try {
      const started = await api.startPoll()
      setJob(started)
      stop()
      timer.current = window.setInterval(() => void check(), POLL_INTERVAL_MS)
    } catch (e) {
      setError(e instanceof Error ? e.message : REFRESH_BUTTON.failed)
    }
  }

  const running = job?.status === 'running'
  const stale =
    !lastCheckedAt || (Date.now() - new Date(lastCheckedAt).getTime()) / 86_400_000 > 2

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.625rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          textAlign: 'right',
          lineHeight: 1.5,
          // Ámbar sólo cuando el dato ya envejeció: si no, es sólo una nota al pie.
          color: stale && !running ? ACCENT : INK_SOFT,
        }}
      >
        {running
          ? `${REFRESH_BUTTON.inProgress} ${Math.round(job?.elapsed_seconds ?? 0)}s`
          : lastCheckedAt
            ? `${REFRESH_BUTTON.lastChecked} ${relativeDays(lastCheckedAt.slice(0, 10), today)}`
            : REFRESH_BUTTON.never}
      </Box>

      <Tooltip title={REFRESH_BUTTON.tooltip}>
        <span>
          <Button
            size="small"
            variant={stale && !running ? 'contained' : 'outlined'}
            onClick={() => void start()}
            disabled={running}
            startIcon={
              running ? (
                <CircularProgress size={13} color="inherit" />
              ) : (
                <RefreshIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ height: 36, fontSize: '0.8125rem' }}
          >
            {running ? REFRESH_BUTTON.running : REFRESH_BUTTON.label}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(result)}
        autoHideDuration={8000}
        onClose={() => setResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setResult(null)}>
          {result}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={10000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
